import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { ChevronDown, ChevronUp, EyeOff, Maximize2, Minimize2, RefreshCw, WifiOff } from '@/components/icons';
import type { LiveEvent } from '@1qa/shared';
import { ApiError, openLiveStream } from '@/lib/api';
import { RECONNECT_DELAYS, parseLiveEvent } from '@/lib/liveView';
import { readSse } from '@/lib/sse';
import { Button, Card, Spinner } from '@/components/ui';

type Phase = 'connecting' | 'waiting' | 'live' | 'retrying' | 'lost' | 'finished' | 'off';

interface CurrentTest {
  testCaseId: string;
  name: string;
  index: number;
  total: number;
}

/**
 * Live view of a run's browser, or of the Android device it runs on (opt-in when the run was started). Streams frames over SSE (fetch with
 * the bearer token), paints only the latest frame per animation frame, and stops when the run ends.
 */
export function LiveView({ runId, runStatus, onFinished }: { runId: string; runStatus: string; onFinished: () => void }) {
  const [phase, setPhase] = useState<Phase>(runStatus === 'QUEUED' ? 'waiting' : 'connecting');
  const [test, setTest] = useState<CurrentTest | null>(null);
  const [notice, setNotice] = useState<{ kind: 'hidden' | 'paused' | 'unavailable'; text: string } | null>(null);
  const [url, setUrl] = useState<string>('');
  /** Set when frames come from a phone or tablet: a portrait frame, labelled with the device. */
  const [device, setDevice] = useState<string>('');
  const [hasFrame, setHasFrame] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [generation, setGeneration] = useState(0);
  const attemptRef = useRef(0);
  const [errorText, setErrorText] = useState('');

  const imgRef = useRef<HTMLImageElement>(null);
  const pendingFrame = useRef<{ data: string; mime: string } | null>(null);
  const rafId = useRef<number | null>(null);
  const finishedRef = useRef(false);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  // Paint only the newest frame once per animation frame (frames may arrive faster than we paint).
  const paint = useCallback(() => {
    rafId.current = null;
    const frame = pendingFrame.current;
    pendingFrame.current = null;
    if (frame && imgRef.current) imgRef.current.src = `data:${frame.mime};base64,${frame.data}`;
  }, []);

  const handle = useCallback(
    (e: LiveEvent) => {
      switch (e.type) {
        case 'hello':
          attemptRef.current = 0;
          setPhase(e.runStatus === 'QUEUED' ? 'waiting' : 'live');
          break;
        case 'test':
          if (e.status === 'RUNNING') {
            setTest({ testCaseId: e.testCaseId, name: e.name, index: e.index, total: e.total });
            setNotice(null);
            setPhase('live');
          }
          break;
        case 'frame':
          setPhase('live');
          if (!hasFrame) setHasFrame(true);
          if (e.url) setUrl(e.url);
          if (e.device) setDevice(e.device);
          // A new frame ends a temporary pause; a permanent "hidden" stays for the rest of the test.
          setNotice((n) => (n?.kind === 'hidden' ? n : null));
          pendingFrame.current = { data: e.data, mime: e.mime === 'image/png' ? 'image/png' : 'image/jpeg' };
          if (rafId.current === null) rafId.current = requestAnimationFrame(paint);
          break;
        case 'hidden':
          setNotice(
            e.paused
              ? { kind: 'paused', text: 'Live view is paused while secret test data is on screen. It continues by itself.' }
              : { kind: 'hidden', text: 'Live view is paused for this test because it types secret test data.' },
          );
          break;
        case 'unavailable':
          setNotice({ kind: 'unavailable', text: e.reason });
          break;
        case 'end':
          finishedRef.current = true;
          setPhase('finished');
          onFinishedRef.current();
          break;
      }
    },
    [hasFrame, paint],
  );
  const handleRef = useRef(handle);
  handleRef.current = handle;

  // Connection with automatic retries (backoff), then a manual "Reconnect".
  useEffect(() => {
    if (phase === 'finished' || phase === 'off' || phase === 'lost') return;
    const ctrl = new AbortController();
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    (async () => {
      try {
        const res = await openLiveStream(runId, ctrl.signal);
        await readSse(res.body!, (msg) => {
          const ev = parseLiveEvent(msg.data);
          if (ev) handleRef.current(ev);
        }, ctrl.signal);
        if (ctrl.signal.aborted || finishedRef.current) return;
        throw new Error('stream closed');
      } catch (err) {
        if (ctrl.signal.aborted || finishedRef.current) return;
        if (err instanceof ApiError && (err.code === 'LIVE_VIEW_OFF' || err.status === 404 || err.status === 403)) {
          setPhase('off');
          setErrorText(err.message);
          return;
        }
        const delay = RECONNECT_DELAYS[attemptRef.current];
        if (delay === undefined) {
          setPhase('lost');
          return;
        }
        setPhase('retrying');
        retryTimer = setTimeout(() => {
          attemptRef.current += 1;
          setGeneration((g) => g + 1);
        }, delay);
      }
    })();

    return () => {
      ctrl.abort();
      if (retryTimer) clearTimeout(retryTimer);
    };
    // Reopen only on a new generation (retry / Reconnect); phase changes inside the stream must not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, generation]);

  useEffect(() => () => {
    if (rafId.current !== null) cancelAnimationFrame(rafId.current);
  }, []);

  const reconnect = () => {
    attemptRef.current = 0;
    setPhase('connecting');
    setGeneration((g) => g + 1);
  };

  if (phase === 'off') {
    return errorText ? (
      <Card className="no-print p-4 text-sm text-slate-500">Live view is not available for this run: {errorText}</Card>
    ) : null;
  }

  const live = phase === 'live';
  const what = device ? `the ${device}` : 'the browser';
  const alt = test ? `Live view of ${what} running ${test.name}` : `Live view of ${what}`;

  return (
    <section
      aria-label="Live view"
      className={clsx(
        'no-print',
        fullscreen && 'fixed inset-0 z-50 flex flex-col bg-slate-950/95 p-4 backdrop-blur-sm sm:p-8',
      )}
    >
      <Card className={clsx('overflow-hidden', fullscreen && 'flex flex-1 flex-col')}>
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide',
              live ? 'bg-rose-600 text-white' : 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
            )}
          >
            <span className={clsx('size-2 rounded-full bg-current', live && 'motion-safe:animate-pulse')} aria-hidden="true" />
            Live
          </span>
          <p className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800 dark:text-slate-100" aria-live="polite">
            {phase === 'finished'
              ? 'Run finished'
              : test
                ? `Test ${test.index} of ${test.total}: ${test.name}`
                : phase === 'waiting'
                  ? 'Waiting for the run to start…'
                  : 'Connecting…'}
          </p>
          <div className="flex items-center gap-1">
            {!collapsed && phase !== 'finished' && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setFullscreen((f) => !f)}
                aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
                icon={fullscreen ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
              />
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setCollapsed((c) => !c);
                setFullscreen(false);
              }}
              aria-expanded={!collapsed}
              icon={collapsed ? <ChevronDown className="size-4" /> : <ChevronUp className="size-4" />}
            >
              {collapsed ? 'Show live view' : 'Hide live view'}
            </Button>
          </div>
        </div>

        {!collapsed && (
          <div className={clsx('bg-slate-900 p-3 sm:p-4', fullscreen && 'flex flex-1 flex-col')}>
            {(device || url) && (
              <div
                className={clsx('mb-2 truncate rounded-md bg-slate-800 px-3 py-1.5 text-xs text-slate-300', device ? 'mx-auto w-fit' : 'font-mono')}
                title={device || url}
              >
                {device || url}
              </div>
            )}
            <div
              className={clsx(
                'relative mx-auto overflow-hidden rounded-lg bg-black ring-1 ring-slate-700',
                device
                  ? // A phone screen: portrait, as tall as fits.
                    fullscreen
                    ? 'aspect-[9/19.5] flex-1'
                    : 'aspect-[9/19.5] h-[70vh] max-h-[760px] max-w-full'
                  : fullscreen
                    ? 'w-full flex-1'
                    : 'aspect-video w-full max-w-5xl',
              )}
            >
              <img
                ref={imgRef}
                alt={alt}
                className={clsx('absolute inset-0 size-full object-contain', !hasFrame && 'invisible')}
              />
              {(!hasFrame || notice || phase !== 'live') && phase !== 'finished' && (
                <Overlay phase={phase} notice={notice} hasFrame={hasFrame} onReconnect={reconnect} />
              )}
              {phase === 'finished' && (
                <div className="absolute inset-0 grid place-items-center bg-black/60 text-sm font-medium text-white">
                  Run finished - results are below.
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
    </section>
  );
}

function Overlay({
  phase,
  notice,
  hasFrame,
  onReconnect,
}: {
  phase: Phase;
  notice: { kind: 'hidden' | 'paused' | 'unavailable'; text: string } | null;
  hasFrame: boolean;
  onReconnect: () => void;
}) {
  let content: ReactNode = null;
  if (phase === 'lost') {
    content = (
      <>
        <WifiOff className="size-6" />
        <span>The live view connection was lost.</span>
        <Button size="sm" variant="secondary" onClick={onReconnect} icon={<RefreshCw className="size-4" />}>
          Reconnect
        </Button>
      </>
    );
  } else if (phase === 'retrying') {
    content = (
      <>
        <Spinner />
        <span>Connection interrupted - reconnecting…</span>
      </>
    );
  } else if (notice) {
    content = (
      <>
        <EyeOff className="size-6" />
        <span className="max-w-md">{notice.text}</span>
      </>
    );
  } else if (!hasFrame) {
    content = (
      <>
        <Spinner />
        <span>{phase === 'waiting' ? 'Waiting for the run to start…' : 'Starting the browser…'}</span>
      </>
    );
  }
  if (!content) return null;
  return (
    <div className={clsx('absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center text-sm text-slate-200', hasFrame ? 'bg-black/70' : 'bg-black')}>
      {content}
    </div>
  );
}
